import { postJson } from "@/domain/common/client/ApiClient"
import { toYearDayTime } from "@/domain/common/utils/DateUtils"
import { adminRoleLabel } from "@/domain/admin/model/Admin"
import {
  AdminChangePasswordRequestPayload,
  AdminGetMeResponsePayload,
  AdminLoginRequestPayload,
  AdminLoginResponsePayload,
} from "@bracket/idl/main/admin/payload/AdminAuthPayloadPt"
import {
  EmptyRequestPayload,
  EmptyResponsePayload,
} from "@bracket/idl/main/common/payload/EmptyRequestPayloadPt"
import type { AdminPayload } from "@bracket/idl/main/admin/model/AdminPt"
import type { AdminAccount, AdminLoginInput } from "@/domain/admin/model/Admin"

interface AdminLoginResult {
  accessToken: string
  expiresInSeconds: number
  admin: AdminAccount
}

interface AdminChangePasswordParam {
  currentPassword: string
  newPassword: string
}

function toAccount(payload: AdminPayload): AdminAccount {
  return {
    id: payload.id,
    loginId: payload.loginId,
    name: payload.name,
    role: payload.role,
    roleLabel: adminRoleLabel(payload.role),
    isLocked: payload.isLocked,
    lastLoginText: toYearDayTime(payload.lastLoginAt),
  }
}

/**
 * 관리자 인증 클라이언트.
 *
 * 경로는 fight-bracket-server 의 `AdminAuthRouter` 와 1:1 대응한다.
 * login 을 뺀 나머지는 Authorization 헤더가 필요하고, 헤더는 `ApiClient` 가 붙인다.
 */
const AdminAuthClient = {
  async login(input: AdminLoginInput): Promise<AdminLoginResult> {
    const request = AdminLoginRequestPayload.create({
      loginId: input.loginId.trim(),
      password: input.password,
    })

    const json = await postJson<unknown>(
      "/bo/auth/login",
      AdminLoginRequestPayload.toJSON(request),
    )
    const response = AdminLoginResponsePayload.fromJSON(json)

    if (!response.admin) throw new Error("로그인 응답이 올바르지 않습니다.")

    return {
      accessToken: response.accessToken,
      expiresInSeconds: response.expiresInSeconds,
      admin: toAccount(response.admin),
    }
  },

  /** 토큰이 가리키는 관리자 — 화면 진입 시 세션 확인에 쓴다 */
  async getMe(): Promise<AdminAccount> {
    const json = await postJson<unknown>(
      "/bo/auth/get-me",
      EmptyRequestPayload.toJSON(EmptyRequestPayload.create({})),
    )
    const response = AdminGetMeResponsePayload.fromJSON(json)

    if (!response.admin) throw new Error("관리자 정보를 찾을 수 없습니다.")

    return toAccount(response.admin)
  },

  async changePassword(param: AdminChangePasswordParam): Promise<void> {
    const request = AdminChangePasswordRequestPayload.create({
      currentPassword: param.currentPassword,
      newPassword: param.newPassword,
    })

    const json = await postJson<unknown>(
      "/bo/auth/change-password",
      AdminChangePasswordRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },
}

export { AdminAuthClient, toAccount }
export type { AdminChangePasswordParam, AdminLoginResult }
